/**
 * SectorBenchmark — an anonymised "how you compare" panel on the lite result.
 * Pick a sector to see the 25th–75th percentile band (and median) of other
 * anonymous respondents vs your own overall. Falls back to the all-sectors band
 * when a sector has too few responses, so the comparison is never misleading.
 */
import React, { useState } from "react";
import axios from "axios";
import { BarChart3, Users } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL + "/api";
const SECTORS = [
  "Manufacturing", "Telecom", "Financial services", "Public sector", "Retail",
  "Healthcare", "Energy & utilities", "Professional services", "Other",
];
const MAX = 4;
const pct = (v) => `${(((v - 1) / (MAX - 1)) * 100).toFixed(1)}%`;

export default function SectorBenchmark({ slug, overall, subBrand }) {
  const [sector, setSector] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (sec) => {
    setSector(sec);
    if (!sec) { setData(null); return; }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE}/public/maturity/sector-benchmark`, {
        params: { slug, sector: sec },
      });
      setData(data);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const band = data?.sector_band || data?.global_band || null;
  const isSector = data?.shown === "sector";

  return (
    <div className="mt-10 border border-zinc-200 bg-white p-6 md:p-7 rounded-lg" data-testid="lite-sector-benchmark">
      <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#7737F6] mb-2 flex items-center gap-2">
        <BarChart3 size={14} /> How you compare
      </div>
      <h3 className="ek-heading text-xl md:text-2xl text-black mb-1">See your {subBrand} maturity against your sector.</h3>
      <p className="text-zinc-600 text-[13px] max-w-2xl mb-4">
        Pick your sector to benchmark your overall score against other anonymised respondents. Fully anonymous — no
        single organisation is ever identifiable.
      </p>

      <select
        value={sector}
        onChange={(e) => load(e.target.value)}
        className="bg-white text-black border border-zinc-300 px-3 py-2.5 text-[15px] rounded w-full md:w-72 mb-5"
        data-testid="sector-benchmark-select"
      >
        <option value="">Choose your sector…</option>
        {SECTORS.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>

      {loading && <div className="text-[13px] text-zinc-500" data-testid="sector-benchmark-loading">Loading benchmark…</div>}

      {!loading && sector && !band && (
        <div className="text-[13px] text-zinc-600 bg-zinc-50 border border-zinc-200 rounded p-4" data-testid="sector-benchmark-empty">
          The benchmark builds as more leaders complete this assessment — you're one of the first. Book a discovery
          conversation below to see how you compare against our full peer dataset.
        </div>
      )}

      {!loading && band && (
        <div data-testid="sector-benchmark-result">
          {/* Scale bar */}
          <div className="relative h-16 mt-2">
            {/* track */}
            <div className="absolute left-0 right-0 top-7 h-1.5 bg-zinc-100 rounded-full" />
            {/* peer band 25th–75th */}
            <div
              className="absolute top-[22px] h-3 bg-[#7737F6]/20 border-x-2 border-[#7737F6]/40 rounded"
              style={{ left: pct(band.low), width: `calc(${pct(band.high)} - ${pct(band.low)})` }}
              data-testid="sector-benchmark-band"
            />
            {/* peer median */}
            <div className="absolute top-[16px] w-px h-7 bg-[#7737F6]" style={{ left: pct(band.median) }} />
            <div className="absolute -translate-x-1/2 text-[10px] text-[#7737F6] font-medium top-0" style={{ left: pct(band.median) }}>
              Peer median {band.median}
            </div>
            {/* your score */}
            <div className="absolute -translate-x-1/2 top-[30px]" style={{ left: pct(overall) }} data-testid="sector-benchmark-you">
              <div className="w-3.5 h-3.5 rounded-full bg-[#0B0B0C] border-2 border-white shadow ring-2 ring-[#0B0B0C]/20" />
              <div className="text-[10px] text-black font-semibold mt-1 -translate-x-1/2 absolute left-1/2 whitespace-nowrap">You {overall}</div>
            </div>
            {/* axis labels */}
            <div className="absolute left-0 bottom-0 text-[10px] text-zinc-400">1</div>
            <div className="absolute right-0 bottom-0 text-[10px] text-zinc-400">4</div>
          </div>

          <div className="flex items-center gap-2 text-[12px] text-zinc-500 mt-3 flex-wrap" data-testid="sector-benchmark-meta">
            <Users size={13} className="text-zinc-400" />
            {isSector ? (
              <span>Based on <strong className="text-black">{band.n}</strong> anonymised {sector} assessments. Peer range {band.low}–{band.high}/4.</span>
            ) : (
              <span>Not enough {sector} responses yet — showing <strong className="text-black">all sectors</strong> ({band.n} respondents). Peer range {band.low}–{band.high}/4.</span>
            )}
          </div>
          <p className="text-[12px] mt-2">
            {overall >= band.median
              ? <span className="text-emerald-700">You're at or above the peer median for this lens.</span>
              : <span className="text-amber-700">You're below the peer median — the full diagnostic shows exactly where to close the gap.</span>}
          </p>
        </div>
      )}
    </div>
  );
}
